import { useEffect, useLayoutEffect, useRef, useState } from "react";

let locks = 0;

// Lock page scroll behind an open menu (overflow only – no position:fixed / touchmove tricks that kill inner scroll on iOS)
export function useScrollLock(on) {
  useEffect(() => {
    if (!on) return;
    if (locks++ === 0) document.documentElement.classList.add("scroll-locked");
    return () => {
      if (--locks === 0) document.documentElement.classList.remove("scroll-locked");
    };
  }, [on]);
}

function fit(el) {
  if (!el) return;
  const vh = window.visualViewport?.height || window.innerHeight;
  const bn = document.querySelector(".bottom-nav");
  const bnH = bn && getComputedStyle(bn).display !== "none" ? bn.getBoundingClientRect().height : 0;
  el.style.maxHeight = `${Math.max(140, Math.floor(vh - el.getBoundingClientRect().top - bnH - 8))}px`;
}

// Cap a dropdown's height to the visible viewport below it (header, bottom nav and safe areas included)
export function useFitViewport(ref, on) {
  useLayoutEffect(() => {
    if (!on) return;
    const el = ref.current;
    const f = () => fit(el);
    f();
    window.addEventListener("resize", f);
    window.visualViewport?.addEventListener("resize", f);
    return () => {
      window.removeEventListener("resize", f);
      window.visualViewport?.removeEventListener("resize", f);
      if (el) el.style.maxHeight = "";
    };
  }, [on, ref]);
}

// Fold the top navigation behind the menu button when its items don't fit on one line.
// Measures the children's real widths (works with overflow:clip) and re-runs on resize, font load and content changes.
export function useNavFold(ref, deps) {
  const [fold, setFold] = useState(false);
  const need = useRef(0);
  const key = deps.join("|");
  useLayoutEffect(() => setFold(false), [key]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const width = () => {
      const cs = getComputedStyle(el);
      const kids = [...el.children].filter((c) => getComputedStyle(c).display !== "none" && !c.classList.contains("spacer"));
      const gap = parseFloat(cs.columnGap) || 0;
      const sum = kids.reduce((s, c) => s + c.getBoundingClientRect().width, 0) + gap * Math.max(0, kids.length - 1);
      return Math.ceil(sum + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight));
    };
    const check = () => {
      if (!fold) {
        const w = Math.max(width(), el.scrollWidth);
        if (w > el.clientWidth + 1) {
          need.current = w;
          setFold(true);
        }
      } else if (el.clientWidth >= need.current + 8) setFold(false);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    [...el.children].forEach((c) => ro.observe(c));
    document.fonts?.ready.then(check);
    document.fonts?.addEventListener?.("loadingdone", check);
    return () => {
      ro.disconnect();
      document.fonts?.removeEventListener?.("loadingdone", check);
    };
  }, [fold, key, ref]);
  return fold;
}
